import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_HOST } from '../../../../core/api/api.config';
import { ApiService } from '../../../../core/api/api.service';
import { ENDPOINTS } from '../../../../core/api/endpoints';
import { Facility } from '../models/models';
import { PaymentDetailsResponse } from '../models/payment-details.model';

@Injectable({
  providedIn: 'root',
})
export class DisbursementService {
  constructor(private api: ApiService) {}

  /* ================= FACILITY ================= */

  sendSelectedFacilities(facilityIds: number[]): Observable<any> {
    const payload = { facilitySeqs: facilityIds };
    return this.api.post(API_HOST.facility, ENDPOINTS.FASCILITIES.SEND_SELECTED, payload);
  }

  getFacilities(): Observable<any> {
    return this.api.get(API_HOST.facility, ENDPOINTS.FASCILITIES.GET_FACILITIES);
  }

  createFacility(facility: Facility): Observable<any> {
    const payload = this.mapFacilityToPayload(facility);
    return this.api.post(API_HOST.facility, ENDPOINTS.FASCILITIES.SAVE_FACILITY, payload);
  }

  createDisbursement(payload: any): Observable<any> {
    return this.api.post(API_HOST.disbursement, ENDPOINTS.DISBURSEMENT.SAVE, payload);
  }
  getAccruedMarkupByFacility(fcltySeq: number): Observable<any> {
    return this.api.get(API_HOST.onboarding, ENDPOINTS.RECOVERY.ACCRUD_MARKUP + fcltySeq);
  }
  getAccruedMarkupByDisbursement(dsbmntSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.payment_accrued,
      ENDPOINTS.RECOVERY.ACCRUD_MARKUP_BY_DSBMNT + dsbmntSeq,
    );
  }

  payAccruedMarkupByDisbursement(
    dsbmntSeq: number,
    monthYear: string,
    payload: any,
  ): Observable<any> {
    return this.api.post(
      API_HOST.payment_accrued,
      ENDPOINTS.RECOVERY.PAY_ACCRUD_MARKUP_BY_DSBMNT + dsbmntSeq + '/' + monthYear,
      payload,
    );
  }
  getDisbursementsByFacility(fcltySeq: number): Observable<any> {
    return this.api.get(API_HOST.facility, ENDPOINTS.DISBURSEMENT.GET_BY_FACILITY + fcltySeq);
  }

  getPaymentSchedule(dsbmntSeq: number): Observable<any> {
    return this.api.get(API_HOST.onboarding, ENDPOINTS.DISBURSEMENT.PAYMENT_SCHEDULE + dsbmntSeq);
  }

  getPaymentDetails(dsbmntSeq: number): Observable<PaymentDetailsResponse> {
    return this.api.get<PaymentDetailsResponse>(
      API_HOST.payment_recovery,
      ENDPOINTS.RECOVERY.PAYMENT_DETAILS + dsbmntSeq,
    );
  }

  updatePaymentSchedules(payload: any): Observable<any> {
    return this.api.put(API_HOST.disbursement, ENDPOINTS.DISBURSEMENT.SCHEDULE_UPDATE, payload);
  }

  getConsolidatedPayments(fcltySeq: number): Observable<any> {
    return this.api.get(API_HOST.facility, ENDPOINTS.DISBURSEMENT.CONSOLIDATED_PAYMENTS + fcltySeq);
  }

  getAccruedIncome(fcltySeq: number): Observable<any> {
    return this.api.get(API_HOST.payment_accrued, ENDPOINTS.RECOVERY.ACCRUED_INCOME + fcltySeq);
  }

  getAccruedIncomeByClient(clientSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.onboarding,
      ENDPOINTS.DISBURSEMENT.ACCRUED_INCOME_CLIENT + clientSeq,
    );
  }
  getPenaltyByFacility(fcltySeq: number): Observable<any> {
    return this.api.get(
      API_HOST.payment_recovery,
      ENDPOINTS.RECOVERY.GET_PENALTY_BY_FACILITY + fcltySeq,
    );
  }
  calculateAccruedMarkup(payload: any): Observable<any> {
    return this.api.post(
      API_HOST.payment_accrued,
      ENDPOINTS.DISBURSEMENT.CALCULATE_ACCRUED,
      payload,
    );
  }

  markAccruedPaid(fcltySeq: number, monthYearOfCalc: string, payload: any): Observable<any> {
    return this.api.post(
      API_HOST.recovery,
      ENDPOINTS.RECOVERY.MARK_ACCRUED_PAID + fcltySeq + '/' + monthYearOfCalc,
      payload,
    );
  }

  markAccruedPaidConsolidatedPayment(payload: any): Observable<any> {
    return this.api.post(
      API_HOST.payment_accrued,
      ENDPOINTS.RECOVERY.MARK_ACCRUED_PAID_consolidated_payment,
      payload,
    );
  }

  markAccruedPaidPartialPayment(payload: any): Observable<any> {
    return this.api.post(
      API_HOST.payment_accrued,
      ENDPOINTS.RECOVERY.MARK_ACCRUED_PAID_partial_payment,
      payload,
    );
  }

  getPendingSchedules(fcltySeq: number): Observable<any> {
    return this.api.get(API_HOST.payment_recovery, ENDPOINTS.RECOVERY.COMPLETE_SCHEDULE + fcltySeq);
  }
  getPendingSchedulesByFacility(fcltySeq: number): Observable<any> {
    return this.api.get(
      API_HOST.payment_recovery,
      ENDPOINTS.RECOVERY.COMPLETE_SCHEDULE_FACILITY + fcltySeq,
    );
  }
  // Schedule - Full Payment: /mark-schedule-paid/{fcltySeq}/{monthYear}
  markSchedulePaid(fcltySeq: number, monthYearOfCalc: string, payload: any): Observable<any> {
    return this.api.post(
      API_HOST.payment_recovery,
      ENDPOINTS.RECOVERY.MARK_SCHEDULE_PAID + fcltySeq + '/' + monthYearOfCalc,
      payload,
    );
  }

  // Schedule PRSP - Full Payment: /mark-schedule-paid-for-phase/{fcltySeq}/{monthYear}
  markSchedulePaidForPhase(
    fcltySeq: number,
    monthYearOfCalc: string,
    payload: any,
  ): Observable<any> {
    return this.api.post(
      API_HOST.payment_recovery,
      ENDPOINTS.RECOVERY.MARK_SCHEDULE_PAID_FOR_PHASE + fcltySeq + '/' + monthYearOfCalc,
      payload,
    );
  }

  // Schedule - Partial Payment: /process-payment/{dsbmntSeq}
  processPartialPayment(dsbmntSeq: number, payload: any): Observable<any> {
    return this.api.post(
      API_HOST.payment_recovery,
      ENDPOINTS.RECOVERY.PROCESS_PAYMENT + dsbmntSeq,
      payload,
    );
  }

  /* ================= DISBURSEMENT DELETE ================= */

  deleteDisbursement(dsbmntSeq: number): Observable<any> {
    return this.api.put(API_HOST.disbursement, ENDPOINTS.DISBURSEMENT.DELETE + dsbmntSeq, {});
  }

  private mapFacilityToPayload(facility: Facility): any {
    const vkbrFlg = Number(facility.vkbrFlg ?? 0);

    return {
      fcltySeq: facility.id && facility.id > 0 ? facility.id : null,

      clientSeq: Number(facility.clientSeq),
      fcltyNm: facility.facilityName,

      phsKey: Number(facility.phase),

      fcltyAprvdAmt: Number(facility.amount),
      fcltyDsprdAmt: Number(facility.fcltyDsprdAmt ?? 0),

      // Prefer explicit dsbmntFlg from the form/model if provided; otherwise derive from disbursed amount
      dsbmntFlg:
        facility.dsbmntFlg !== undefined && facility.dsbmntFlg !== null
          ? Number(facility.dsbmntFlg)
          : Number(facility.fcltyDsprdAmt ?? 0) > 0
            ? 1
            : 0,

      markupFlg:
        facility.markupFlg !== undefined && facility.markupFlg !== null
          ? Number(facility.markupFlg)
          : 0,

      stsKey: Number(facility.status),
      fcltyTypKey: facility.fcltyTypKey,

      aprvlDt: this.toIsoDateTime(facility.date),
      fcltyEndDt: this.toIsoDateTime(facility.fcltyEndDt),

      sprdRt: Number(facility.spreadRate ?? 0),
      kbrRt: Number(facility.kiborRate ?? 0),
      kbrRstPrd: Number(facility.kiborResetPeriod ?? 0),

      // Kibor Variable Day: Always 0 (not user input)
      acrdCalcFlg: 0,
      acrdCalcDay: null,

      // V-KIBOR Fields (Active Kibor Per Day)
      vkbrFlg: vkbrFlg,
      vkbrRt: vkbrFlg === 1 ? Number(facility.vkbrRt ?? 0) : null,
      vkbrStrtDt: vkbrFlg === 1 ? this.toIsoDateTime(facility.vkbrStrtDt) : null,
      vkbrEndDt: vkbrFlg === 1 ? this.toIsoDateTime(facility.vkbrEndDt) : null,

      delFlg: 0,
    };
  }

  private toIsoDateTime(date?: string | null): string {
    if (!date) {
      return new Date().toISOString();
    }
    return new Date(date).toISOString();
  }

  private mapFrequencyKey(freq?: string | null): number {
    return freq === 'Quarterly' ? 2 : 1; // Monthly default
  }
}
